/**
 * Right-aligned numeric table cells (TBL-002, TBL-010, TMMRSL-0057). The theme supplies tabular
 * numerals for `align="right"`. A missing value is "—", never 0.
 */
import type { ReactNode } from 'react';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import { Box, TableCell, TableFooter, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatSignedMoney, moneyIn } from '@/components/moneyIn';
import {
  bpToPercentText,
  formatCount,
  formatMoneyAccounting,
  formatSignedCount,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';

const nowrap = { whiteSpace: 'nowrap' } as const;

/** A money amount in its own currency. `signed` adds an explicit plus on a credit. */
export function MoneyCell({
  minor,
  currency,
  sx = [],
  children,
  signed = false,
}: {
  minor: number | null | undefined;
  currency: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  signed?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const text =
    minor == null
      ? dash
      : signed
        ? formatSignedMoney(minor, currency, locale, dash)
        : moneyIn(minor, currency, locale, dash);
  return (
    <TableCell align="right" sx={[nowrap, ...(Array.isArray(sx) ? sx : [sx])]}>
      {text}
      {children}
    </TableCell>
  );
}

/**
 * A data table's totals row, in its `TableFooter` (TBL-014, TMMRSL-0073): body-size bold text ruled
 * off from the rows, as `PlanExample`'s. The label cell is the row header (`component="th"
 * scope="row"`), which the caller renders.
 */
export function TotalsFooter({ children }: { children: ReactNode }) {
  return (
    <TableFooter>
      <TableRow
        sx={{
          '& > *': {
            fontWeight: 600,
            color: 'text.primary',
            fontSize: 'inherit',
            borderTop: 1,
            borderTopColor: 'divider',
          },
        }}
      >
        {children}
      </TableRow>
    </TableFooter>
  );
}

/**
 * A count, a signed change, or a basis-point percent. `percentBp` renders through
 * `bpToPercentText` (the `%` is part of that string).
 */
export function NumericCell({
  value,
  signed = false,
  percentBp = false,
}: {
  value: number | null | undefined;
  signed?: boolean;
  percentBp?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  let text = dash;
  if (value != null) {
    if (percentBp) text = bpToPercentText(value, locale);
    else if (signed) text = formatSignedCount(value, locale);
    else text = formatCount(value, locale);
  }
  return (
    <TableCell align="right" sx={nowrap}>
      {text}
    </TableCell>
  );
}

/** The class a negative accounting amount carries, beside its error-text colour. */
export const NEGATIVE_AMOUNT_CLASS = 'amount-negative';

/**
 * An amount in accounting format (`formatMoneyAccounting`, TMMRSL-0075): a negative is in the
 * locale's accounting pattern (parentheses in en and fr) and in the error-text colour, never colour
 * alone (WCAG 1.4.1). Tabular numerals come from the caller's right-aligned cell or figure.
 */
export function AccountingAmount({ minor, currency }: { minor: number; currency: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  let text = t('statements.unavailable');
  if (currency) {
    try {
      text = formatMoneyAccounting(minor, currency, locale);
    } catch {
      // A currency Intl doesn't know: the dash.
    }
  }
  if (minor >= 0) return <>{text}</>;
  return (
    <Box component="span" className={NEGATIVE_AMOUNT_CLASS} sx={{ color: tokens.errorText }}>
      {text}
    </Box>
  );
}

/** A right-aligned table cell holding an `AccountingAmount`. */
export function AccountingMoneyCell({ minor, currency }: { minor: number; currency: string }) {
  return (
    <TableCell align="right" sx={nowrap}>
      <AccountingAmount minor={minor} currency={currency} />
    </TableCell>
  );
}
